/**
 * The VehicleBody3D rule: only what a VehicleBody3D adds to a RigidBody3D. Godot has
 * no 2D vehicle body, but ruleCoverage derives the rule name from the factory shape.
 * Repeating a shared check, such as the scale warning of rigid_body_3d.cpp:667, would
 * report one condition under two rule names.
 */
import { armEmits, groundedArm, reportArm } from '../ruleArms.js';
import { dimSuffix } from './dim.js';
import { hasChildOfType } from '../childType.js';
/**
 * `_update_friction` returns before any suspension or traction impulse is
 * computed when `wheels` is empty. Not dimension-keyed: Godot declares no
 * VehicleBody2D, so this factory serves one family.
 */
const NO_WHEELS_AT = 'vehicle_body_3d.cpp:731';
/**
 * `rigidBodyLinterRule` reaches this type through `descendsFromClass` with the shared body
 * set and the per-axis scale warning, so no `vehiclebody3d-scaled-transform` repeats
 * rigid_body_3d.cpp:667 here. `collisionObjectLinterRule` supplies the no-shape warning.
 */
export function makeVehicleBodyLinterRule(dim) {
    const type = `VehicleBody${dim}`;
    const wheelType = `VehicleWheel${dim}`;
    const prefix = `vehiclebody${dimSuffix(dim)}`;
    const arms = {
        needsWheels: groundedArm(`${prefix}-needs-wheels`, {
            kind: 'engine-inert',
            at: NO_WHEELS_AT,
            unused: 'with no wheels the suspension and traction pass returns before applying anything',
        }),
    };
    function check(context) {
        const diagnostics = [];
        const { node } = context;
        // With no wheels, engine_force and steering do nothing and the body acts as a
        // plain RigidBody3D. VehicleWheel3D registers in NOTIFICATION_ENTER_TREE through
        // `cast_to<VehicleBody3D>(get_parent())`, so a nested wheel is never attached.
        if (!hasChildOfType(node, [wheelType])) {
            reportArm(diagnostics, arms.needsWheels, node, `${type} '${node.name}' has no direct ${wheelType} children. A vehicle body is driven by its wheels, and Godot only attaches wheels that are its immediate children; without them engine_force and steering have no effect.`);
        }
        return diagnostics;
    }
    return {
        meta: {
            name: `valid-${prefix}`,
            description: `Validates that a ${type} has wheels`,
            category: 'validation',
            applicableNodeTypes: [type],
            emits: armEmits(arms),
        },
        check,
    };
}
//# sourceMappingURL=vehicleBodyLinterRule.js.map